<template>
  <div>
    <v-row>
      <v-col cols="12" md="4" lg="3" xl="2">
        <v-card outlined tile>
          <v-card-title>
            Modules
            <v-spacer />
            <v-switch
              v-model="showArchived"
              :label="showArchived ? 'Archived' : 'Active'"
            />
          </v-card-title>
          <v-list
            two-line
            subheader
          >
            <v-list-item-group v-model="selectedModuleId">
              <v-list-item v-for="module of modules" :key="module._id" :value="module._id" three-line>
                <v-list-item-content>
                  <v-list-item-title>{{ module.code }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ module.units.length }} {{ 'unit' | pluralize(module.units.length) }},
                    {{ module.units.filter(o => o.active).length }} active</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <v-card-actions>
            <v-btn
              text
              color="primary"
              @click="addModule"
            >
              <v-icon>add</v-icon>
              Add module
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" md="8" lg="9" xl="10" v-if="selectedModuleId && selectedModule">
        <v-row>
          <v-col>
            <v-card color="teal lighten-5">
              <v-card-title>Module Details</v-card-title>

              <v-row class="pa-4">
                <v-col cols="12">
                  <div class="text-subtitle-2">Description</div>
                  <p v-if="selectedModule.description">{{ selectedModule.description }}</p>
                  <p v-else>No description provided.</p>
                </v-col>
              </v-row>

              <v-card-actions>
                <v-btn
                  text
                  @click="editModule(selectedModule)"
                >
                  Edit
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-card color="teal lighten-5">
              <v-card-title>
                Module Units
                <v-spacer />
                <v-switch
                  v-model="showArchivedUnits"
                  :label="showArchivedUnits ? 'Archived' : 'Active'"
                />
            </v-card-title>
              <template v-if="selectedModule !== null">
                <v-card
                  v-for="unit of selectedModule.units"
                  v-show="unit.active || showArchivedUnits"
                  :key="unit._id"
                  outlined
                  flat
                  tile
                  class="mx-2 mb-2"
                  :color="unit.active ? null : 'grey lighten-3'"
                >
                  <v-row class="pa-4">
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-subtitle-2">Unit Code</div>
                      <p>{{ unit.code }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-subtitle-2">Description</div>
                      <p v-if="unit.description">{{ unit.description }}</p>
                      <p v-else class="font-italic">No description provided.</p>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-subtitle-2">Target</div>
                      <p>{{ unit.target }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-subtitle-2">Duration</div>
                      <p>{{ unit.duration }}</p>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="text-subtitle-2">Send to AUS-MEAT</div>
                      <p>{{ unit.enableAusMeatIntegration | yesNo }}</p>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="text-subtitle-2">Objective</div>
                      <p v-if="unit.objective">{{ unit.objective }}</p>
                      <p v-else class="font-italic">No objective provided.</p>
                    </v-col>
                  </v-row>

                  <v-card-actions>
                    <v-btn
                      text
                      @click="editUnit(unit)"
                    >
                      Edit
                    </v-btn>
                  </v-card-actions>
                </v-card>
                <v-card-actions>
                  <v-btn
                    text
                    color="primary"
                    @click="addUnit"
                  >
                    <v-icon>add</v-icon>
                    Add unit
                  </v-btn>
                </v-card-actions>
              </template>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-dialog
      v-model="moduleDialog"
      width="800"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ moduleForm.code || 'New Module' }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            name="moduleCode"
            label="Module Code"
            v-model="moduleForm.code"
            placeholder="MOD-XXX"
          />
          <v-text-field
            name="description"
            label="Description"
            v-model="moduleForm.description"
          />
          <v-checkbox
            name="active"
            label="Active"
            v-model="moduleForm.active"
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="saveModule"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="moduleDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="unitDialog"
      width="800"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ unitForm.code || 'New Unit' }}
        </v-card-title>

        <v-card-text>
          <v-form>
            <v-text-field
              name="unitCode"
              label="Unit Code"
              v-model="unitForm.code"
              placeholder="MOD-XXX-YYY"
            />
            <v-text-field
              name="description"
              label="Description"
              v-model="unitForm.description"
            />
            <v-text-field
              name="objective"
              label="Objective"
              v-model="unitForm.objective"
            />
            <v-text-field
              name="target"
              label="Target"
              v-model="unitForm.target"
            />
            <v-text-field
              name="duration"
              label="Duration"
              v-model="unitForm.duration"
            />
            <v-checkbox
              name="active"
              label="Active"
              v-model="unitForm.active"
            />
            <v-checkbox
              name="enableAusMeatIntegration"
              label="Send to AUS-MEAT"
              v-model="unitForm.enableAusMeatIntegration"
            />
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="saveUnit"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="unitDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'

export default {
  name: 'TrainingModuleList',

  data: () => ({
    selectedModuleId: null,
    selectedModule: null,
    modules: [],
    moduleDialog: false,
    moduleForm: {},
    unitDialog: false,
    unitForm: {},
    showArchived: false,
    showArchivedUnits: false
  }),

  apollo: {
    modules: {
      query: gql`query TrainingModulesList($filter: FilterFindManyTrainingModuleInput!) {
        modules: trainingModuleMany(limit: 0, sort: CODE_ASC, filter: $filter) {
          _id
          code
          units {
            _id
            active
          }
        }
      }`,
      variables () {
        return {
          filter: {
            active: !this.showArchived
          }
        }
      },
      error (e) { this.onError(e) }
    },
    selectedModule: {
      query: gql`query TrainingModuleDetails($id: MongoID!) {
        selectedModule: trainingModuleById(_id: $id) {
          _id
          code
          description
          active
          units {
            _id
            code
            description
            objective
            target
            duration
            active
            enableAusMeatIntegration
          }
        },
      }`,
      variables () {
        return {
          id: this.selectedModuleId
        }
      },
      skip () {
        return !this.selectedModuleId
      },
      error (e) { this.onError(e) }
    }
  },

  methods: {
    addModule () {
      this.moduleForm = {
        code: null,
        description: null,
        active: true
      }
      this.moduleDialog = true
    },

    editModule (module) {
      this.moduleForm = {
        _id: module._id,
        code: module.code,
        description: module.description,
        active: module.active
      }
      this.moduleDialog = true
    },

    addUnit () {
      this.unitForm = {
        code: null,
        description: null,
        objective: null,
        target: null,
        duration: null,
        active: true,
        enableAusMeatIntegration: false
      }
      this.unitDialog = true
    },

    editUnit (unit) {
      this.unitForm = {
        _id: unit._id,
        code: unit.code,
        description: unit.description,
        objective: unit.objective,
        target: unit.target,
        duration: unit.duration,
        active: unit.active,
        enableAusMeatIntegration: unit.enableAusMeatIntegration
      }
      this.unitDialog = true
    },

    onError (e) {
      this.$emit('error', e)
    },

    async saveModule () {
      let mutation
      const id = this.moduleForm._id
      const record = {
        code: this.moduleForm.code,
        description: this.moduleForm.description,
        active: this.moduleForm.active
      }
      if (id) {
        mutation = {
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdTrainingModuleInput!) {
            trainingModuleUpdateById(_id: $id, record: $record) { recordId }
          }`,
          variables: {
            id,
            record
          }
        }
      } else {
        mutation = {
          mutation: gql`mutation ($record: CreateOneTrainingModuleInput!) {
            trainingModuleCreateOne(record: $record) { recordId }
          }`,
          variables: {
            record
          }
        }
      }

      try {
        await this.$apollo.mutate(mutation)
      } catch (e) {
        this.onError(e)
        return
      }

      this.moduleDialog = false
      this.$emit('info', 'Saved')

      this.$apollo.queries.modules.refresh()
    },

    async saveUnit () {
      const id = this.unitForm._id
      const units = this.selectedModule.units
      if (id) {
        Object.assign(units[units.findIndex(o => o._id === id)], this.unitForm)
      } else {
        units.push(this.unitForm)
      }

      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdTrainingModuleInput!) {
            trainingModuleUpdateById(_id: $id, record: $record) { recordId }
          }`,
          variables: {
            id: this.selectedModuleId,
            record: {
              units
            }
          }
        })
      } catch (e) {
        this.onError(e)
        return
      }
      this.unitDialog = false
      this.$apollo.queries.modules.refresh()
      this.$emit('info', 'Unit saved')
    }
  }
}
</script>
